import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { Input } from "../../components/ui/input";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Clipboard,
  FileText,
  CheckCircle,
  AlertTriangle,
  Plus,
  Trash2,
  GripVertical,
  X,
} from "lucide-react";
import {
  getProjectById,
  getTemplateById,
} from "../../api/projectManagementApi";
import { SettingsStepProps } from "./types";
import { AutoGradingSection } from "../../components/AutoGradingSection";

export const ProjectSettingsStep = React.memo<SettingsStepProps>(
  ({
    projectData,
    setProjectData,
    updateRubricItem, // Unused - full rubric implementation would use this
    totalMarks,
    isValidMarks,
    marksError,
    temporaryProjectId,
    setTempCheckPointData, // Unused - preserved for future use
    setBaseProjectData, // Unused - preserved for future use
  }) => {
    const [rubricVariation, setRubricVariation] = useState<
      "compact" | "standard" | "detailed"
    >("detailed");
    const [searchParams] = useSearchParams();
    const { projectId } = useParams();
    const [isInitialized, setIsInitialized] = useState(false);
    const mode = searchParams.get("mode");
    const action = searchParams.get("action");
    const resolvedId = projectId || temporaryProjectId;

    const effectiveId = mode === "edit" ? projectId : resolvedId;

    const { data, isLoading } = useQuery({
      queryKey: ["project", effectiveId],
      queryFn: () => getProjectById(effectiveId || ""),
      enabled:
        !!effectiveId &&
        ((mode === "edit" && !!projectId) ||
          (mode !== "template" && !!resolvedId)),
    });

    const { data: templateData, isLoading: templateLoading } = useQuery({
      queryKey: ["template", resolvedId],
      queryFn: () => getTemplateById(resolvedId || ""),
      enabled: !!resolvedId && mode === "template" && action === "edit",
      gcTime: Infinity,
      staleTime: Infinity,
    });

    // Pre-fill template data in edit mode
    useEffect(() => {
      if (templateData && action === "edit" && mode === "template") {
        setProjectData((prev: any) => {
          const projectDataFromTemplate = templateData as any;

          return {
            ...prev,
            ...projectDataFromTemplate,
            title: projectDataFromTemplate.template_name || prev.title,
            description:
              projectDataFromTemplate.template_description || prev.description,
            estimatedHours:
              projectDataFromTemplate.estimated_hours || prev.estimatedHours,
            saveAsTemplate: projectDataFromTemplate.is_template,
            dueDate: projectDataFromTemplate.due_date,
            prerequisites: projectDataFromTemplate.prerequisites,
            projectType: projectDataFromTemplate.type,
            project_overview: projectDataFromTemplate.project_overview,
            learningObjectives: projectDataFromTemplate.learning_objectives,
            totalPoints: projectDataFromTemplate.total_points,
            total_points: projectDataFromTemplate.total_points,
            templateCategory: (templateData as any)?.template?.category,
            templateSubCategory: (templateData as any)?.template?.subcategory,
            lateSubmissionsAllowed:
              projectDataFromTemplate.late_submission_allowed,
            maxSubmissions: projectDataFromTemplate.max_attempts,
            late_submission_days_allowed:
              projectDataFromTemplate.late_submission_days_allowed === 0
                ? 1
                : projectDataFromTemplate.late_submission_days_allowed,
            gradingRubric: (projectDataFromTemplate.gradingRubric ?? []).map(
              (ru: any) => ({
                ...ru,
                project_id: projectDataFromTemplate.id,
              }),
            ),
          };
        });
      }
    }, [templateData, action, mode, setProjectData]);

    // Initialize project data from fetched data
    useEffect(() => {
      if (mode === "template" || action === "edit") return;
      if (!data || isInitialized) return;

      const initializedData: any = {
        ...data,
        lateSubmissionsAllowed: data.late_submission_allowed,
        maxSubmissions: data.max_attempts,
        late_submission_days_allowed:
          data.late_submission_days_allowed === 0
            ? 1
            : data.late_submission_days_allowed,
        gradingRubric: ((data as any).gradingRubric ?? []).map((ru: any) => ({
          ...ru,
          project_id: data.id,
        })),
      };

      setProjectData(initializedData);
      setIsInitialized(true);
    }, [data, isInitialized, setProjectData, mode, action]);

    // Reset initialization when ID changes
    useEffect(() => {
      setIsInitialized(false);
    }, [resolvedId]);

    // Update grading rubric project IDs
    useEffect(() => {
      if (!projectData.id) return;

      setProjectData((prev: any) => {
        const updated = (prev.gradingRubric ?? [])?.map((ru: any) => ({
          ...ru,
          project_id: prev.id,
        }));
        return { ...prev, gradingRubric: updated };
      });
    }, [projectData.id, setProjectData]);

    const targetTotal = projectData.total_points ?? projectData.totalPoints;

    return (
      <div className="space-y-6">
        {isLoading || templateLoading ? (
          <Card className="h-[50vh]">
            <p className="flex justify-center items-center h-full">
              Fetching Project Details
            </p>
          </Card>
        ) : (
          <>
            {/* Submission Settings */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <Clipboard className="h-5 w-5 text-bits-blue" />
                  <CardTitle>Submission Settings</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-row space-x-6 mb-4">
                  <div className="flex flex-col mb-4 gap-2">
                    <Label>Allow Late Submissions</Label>
                    <Switch
                      checked={projectData.lateSubmissionsAllowed || false}
                      onCheckedChange={(checked) =>
                        setProjectData((prev: any) => ({
                          ...prev,
                          lateSubmissionsAllowed: checked,
                          late_submission_allowed: checked,
                        }))
                      }
                    />
                  </div>
                  <div className="flex flex-col mb-4 gap-2">
                    <Label>Days allowed after due date</Label>
                    <Input
                      type="text"
                      value={
                        (projectData as any).late_submission_days_allowed ===
                          null ||
                        (projectData as any).late_submission_days_allowed ===
                          undefined
                          ? ""
                          : (projectData as any).late_submission_days_allowed
                      }
                      onChange={(e) => {
                        const val = e.target.value;

                        if (!projectData.lateSubmissionsAllowed) {
                          if (val === "0" || val === "") {
                            setProjectData((prev) => ({
                              ...prev,
                              late_submission_days_allowed: 0,
                            }));
                          }
                          return;
                        }

                        if (val === "") {
                          setProjectData((prev) => ({
                            ...prev,
                            late_submission_days_allowed: undefined,
                          }));
                          return;
                        }

                        if (!/^\d+$/.test(val)) {
                          return;
                        }

                        setProjectData((prev) => ({
                          ...prev,
                          late_submission_days_allowed: Number(val),
                        }));
                      }}
                      placeholder={
                        projectData.lateSubmissionsAllowed
                          ? "Enter number of days"
                          : "0"
                      }
                      disabled={!projectData.lateSubmissionsAllowed}
                      className={`bg-bits-grey-100 p-2 rounded-md border ${
                        !projectData.lateSubmissionsAllowed
                          ? "opacity-60 cursor-not-allowed"
                          : ""
                      }`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label>Maximum Submission</Label>
                    <input
                      type="number"
                      min={1}
                      value={projectData.maxSubmissions ?? ""}
                      onChange={(e) =>
                        setProjectData((prev: any) => ({
                          ...prev,
                          maxSubmissions:
                            e.target.value === ""
                              ? null
                              : Number(e.target.value),
                        }))
                      }
                      className="bg-bits-grey-100 p-2 rounded-md border w-full"
                    />
                  </div>
                </div>
                {(mode === "create" ||
                  (mode === "template" && action === "edit")) && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                    <div className="space-y-3">
                      <Label>Save as Template</Label>
                      <div className="flex items-center space-x-2">
                        <Switch
                          checked={projectData.saveAsTemplate || false}
                          onCheckedChange={(checked) =>
                            setProjectData((prev: any) => ({
                              ...prev,
                              saveAsTemplate: checked,
                            }))
                          }
                        />
                        <Label className="font-normal">
                          {projectData.saveAsTemplate ? "Yes" : "No"}
                        </Label>
                      </div>
                    </div>

                    {projectData.saveAsTemplate && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="templateCategory">
                            Template Category *
                          </Label>
                          <Input
                            id="templateCategory"
                            value={projectData.templateCategory || ""}
                            onChange={(e) =>
                              setProjectData((prev: any) => ({
                                ...prev,
                                templateCategory: e.target.value,
                              }))
                            }
                            placeholder="Enter template category"
                            className="text-bits-grey-500 bg-bits-grey-100"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="templateSubCategory">
                            Template Sub Category *
                          </Label>
                          <Input
                            id="templateSubCategory"
                            value={projectData.templateSubCategory || ""}
                            onChange={(e) =>
                              setProjectData((prev: any) => ({
                                ...prev,
                                templateSubCategory: e.target.value,
                              }))
                            }
                            placeholder="Enter template sub category"
                            className="text-bits-grey-500 bg-bits-grey-100"
                          />
                        </div>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* ── Auto Grading ── */}
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <CardTitle>Auto Grading</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <AutoGradingSection
                  projectData={projectData}
                  setProjectData={setProjectData}
                  projectId={resolvedId || undefined}
                />
              </CardContent>
            </Card>

            {/* Grading Rubric */}
            <Card>
              <CardHeader className="border-b border-gray-200 bg-white p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <FileText className="h-4 w-4 text-blue-600" />
                    <div>
                      <CardTitle className="text-base">
                        Grading Rubric
                      </CardTitle>
                      <p className="text-xs text-gray-500">
                        Define grading criteria for checkpoints
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-3">
                    {/* Design Variation Selector */}
                    <Select
                      value={rubricVariation}
                      onValueChange={(
                        value: "compact" | "standard" | "detailed",
                      ) => setRubricVariation(value)}
                    >
                      <SelectTrigger className="w-30 h-8 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="compact">Compact</SelectItem>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="detailed">Detailed</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Total Score</div>
                      <div
                        className={`text-lg font-semibold ${
                          totalMarks === targetTotal
                            ? "text-green-600"
                            : totalMarks > targetTotal
                              ? "text-red-600"
                              : totalMarks > 0
                                ? "text-yellow-600"
                                : "text-gray-500"
                        }`}
                      >
                        {totalMarks}/{targetTotal}
                      </div>
                    </div>
                    <div
                      className={`w-2 h-8 rounded-full ${
                        isValidMarks
                          ? "bg-green-500"
                          : totalMarks > targetTotal
                            ? "bg-red-500"
                            : "bg-yellow-500"
                      }`}
                    ></div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="p-0">
                {rubricVariation === "compact" && (
                  <div className="p-6 space-y-4">
                    {projectData.checkpoints.map(
                      (checkpoint, checkpointIndex) => (
                        <div
                          key={`compact-rubric-checkpoint-${checkpointIndex}`}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-medium text-gray-900">
                              {checkpoint.title}
                            </h3>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                const newRubric = {
                                  name: `Rubric ${(checkpoint.rubrics?.length || 0) + 1}`,
                                  description: "",
                                  points: 0,
                                };
                                const updatedCheckpoints = [
                                  ...projectData.checkpoints,
                                ];
                                if (
                                  !updatedCheckpoints[checkpointIndex].rubrics
                                ) {
                                  updatedCheckpoints[checkpointIndex].rubrics =
                                    [];
                                }
                                updatedCheckpoints[
                                  checkpointIndex
                                ].rubrics.push(newRubric);
                                setProjectData((prev) => ({
                                  ...prev,
                                  checkpoints: updatedCheckpoints,
                                }));
                              }}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add
                            </Button>
                          </div>

                          <div className="space-y-2">
                            {Array.isArray(checkpoint.rubrics)
                              ? checkpoint.rubrics.map(
                                  (rubric, rubricIndex) => {
                                    if (!rubric) return null;
                                    return (
                                      <div
                                        key={`compact-rubric-${rubric.name || rubricIndex}`}
                                        className="flex items-center gap-3 p-2 bg-gray-50 rounded"
                                      >
                                        <span className="text-sm text-gray-500 w-6">
                                          {rubricIndex + 1}.
                                        </span>
                                        <Input
                                          placeholder="Rubric description"
                                          value={rubric.description || ""}
                                          onChange={(e) => {
                                            const updatedCheckpoints = [
                                              ...projectData.checkpoints,
                                            ];
                                            if (
                                              !updatedCheckpoints[
                                                checkpointIndex
                                              ]?.rubrics?.[rubricIndex]
                                            )
                                              return;
                                            updatedCheckpoints[
                                              checkpointIndex
                                            ].rubrics[rubricIndex].description =
                                              e.target.value;
                                            setProjectData((prev) => ({
                                              ...prev,
                                              checkpoints: updatedCheckpoints,
                                            }));
                                          }}
                                          className="flex-1 text-sm"
                                        />
                                        <input
                                          type="text"
                                          value={Math.floor(
                                            rubric.points ??
                                              rubric.total_points ??
                                              0,
                                          ).toString()}
                                          onChange={(e) => {
                                            const val = e.target.value;

                                            // Allow empty string or digits only
                                            if (
                                              val === "" ||
                                              /^\d+$/.test(val)
                                            ) {
                                              const newPoints =
                                                val === ""
                                                  ? 0
                                                  : Math.floor(
                                                      parseInt(val, 10),
                                                    );
                                              const updatedCheckpoints = [
                                                ...projectData.checkpoints,
                                              ];

                                              if (
                                                !updatedCheckpoints[
                                                  checkpointIndex
                                                ]?.rubrics?.[rubricIndex]
                                              )
                                                return;

                                              // Update both points and total_points for consistency
                                              updatedCheckpoints[
                                                checkpointIndex
                                              ].rubrics[rubricIndex].points =
                                                newPoints;
                                              updatedCheckpoints[
                                                checkpointIndex
                                              ].rubrics[
                                                rubricIndex
                                              ].total_points = newPoints;

                                              // Update rubric criteria if exists
                                              if (
                                                updatedCheckpoints[
                                                  checkpointIndex
                                                ].rubrics[rubricIndex].criteria
                                              ) {
                                                if (
                                                  updatedCheckpoints[
                                                    checkpointIndex
                                                  ].rubrics[rubricIndex]
                                                    .criteria.length === 0
                                                ) {
                                                  updatedCheckpoints[
                                                    checkpointIndex
                                                  ].rubrics[
                                                    rubricIndex
                                                  ].criteria = [
                                                    {
                                                      name:
                                                        updatedCheckpoints[
                                                          checkpointIndex
                                                        ].rubrics[rubricIndex]
                                                          .name || "Criterion",
                                                      description:
                                                        updatedCheckpoints[
                                                          checkpointIndex
                                                        ].rubrics[rubricIndex]
                                                          .description || "",
                                                      points: newPoints,
                                                    },
                                                  ];
                                                } else {
                                                  updatedCheckpoints[
                                                    checkpointIndex
                                                  ].rubrics[
                                                    rubricIndex
                                                  ].criteria[0].points =
                                                    newPoints;
                                                }
                                              }

                                              setProjectData((prev) => ({
                                                ...prev,
                                                checkpoints: updatedCheckpoints,
                                              }));
                                            }
                                          }}
                                          className="w-16 text-center text-sm border border-gray-300 rounded px-2 py-1"
                                        />

                                        <Button
                                          variant="ghost"
                                          size="sm"
                                          onClick={() => {
                                            const updatedCheckpoints = [
                                              ...projectData.checkpoints,
                                            ];
                                            updatedCheckpoints[
                                              checkpointIndex
                                            ].rubrics = (
                                              updatedCheckpoints[
                                                checkpointIndex
                                              ].rubrics || []
                                            ).filter(
                                              (_, i) => i !== rubricIndex,
                                            );
                                            setProjectData((prev) => ({
                                              ...prev,
                                              checkpoints: updatedCheckpoints,
                                            }));
                                          }}
                                          className="h-8 w-8 p-0 text-red-500 hover:bg-red-50"
                                        >
                                          <X className="h-4 w-4" />
                                        </Button>
                                      </div>
                                    );
                                  },
                                )
                              : []}

                            {(!Array.isArray(checkpoint.rubrics) ||
                              checkpoint.rubrics.length === 0) && (
                              <div className="text-center py-4 text-gray-500 text-sm">
                                No rubrics defined yet
                              </div>
                            )}
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                )}

                {rubricVariation === "standard" && (
                  <div className="space-y-6 p-6">
                    {projectData.checkpoints.map(
                      (checkpoint, checkpointIndex) => (
                        <div
                          key={`standard-rubric-checkpoint-${checkpointIndex}`}
                          className="border border-gray-200 rounded-lg overflow-hidden"
                        >
                          <div className="bg-gray-50 p-4 border-b border-gray-200">
                            <div className="flex items-center justify-between">
                              <div>
                                <h3 className="font-semibold text-gray-900">
                                  {checkpoint.title}
                                </h3>
                                <p className="text-sm text-gray-600 mt-1">
                                  {Array.isArray(checkpoint.rubrics)
                                    ? checkpoint.rubrics.length
                                    : 0}{" "}
                                  rubrics •{" "}
                                  {Array.isArray(checkpoint.rubrics)
                                    ? checkpoint.rubrics.reduce(
                                        (sum, rubric) =>
                                          sum +
                                          Number(rubric.total_points || 0),
                                        0,
                                      )
                                    : 0}{" "}
                                  points
                                </p>
                              </div>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  const newRubric = {
                                    name: `Rubric ${(checkpoint.rubrics?.length || 0) + 1}`,
                                    description: "",
                                    points: 0,
                                  };
                                  const updatedCheckpoints = [
                                    ...projectData.checkpoints,
                                  ];
                                  if (
                                    !updatedCheckpoints[checkpointIndex].rubrics
                                  ) {
                                    updatedCheckpoints[
                                      checkpointIndex
                                    ].rubrics = [];
                                  }
                                  updatedCheckpoints[
                                    checkpointIndex
                                  ].rubrics.push(newRubric);
                                  setProjectData((prev) => ({
                                    ...prev,
                                    checkpoints: updatedCheckpoints,
                                  }));
                                }}
                                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Rubric
                              </Button>
                            </div>
                          </div>

                          <div className="p-4">
                            <div className="space-y-4">
                              {Array.isArray(checkpoint.rubrics)
                                ? checkpoint.rubrics.map(
                                    (rubric, rubricIndex) => (
                                      <div
                                        key={`standard-rubric-${rubric.name || rubricIndex}`}
                                        className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg bg-white hover:shadow-sm transition-shadow"
                                      >
                                        <div className="w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-700 rounded-lg font-medium text-sm">
                                          {rubricIndex + 1}
                                        </div>
                                        <div className="flex-1">
                                          <Input
                                            placeholder="Describe the grading rubric..."
                                            value={rubric.description || ""}
                                            onChange={(e) => {
                                              const updatedCheckpoints = [
                                                ...projectData.checkpoints,
                                              ];
                                              if (
                                                !updatedCheckpoints[
                                                  checkpointIndex
                                                ]?.rubrics?.[rubricIndex]
                                              )
                                                return;
                                              updatedCheckpoints[
                                                checkpointIndex
                                              ].rubrics[
                                                rubricIndex
                                              ].description = e.target.value;
                                              setProjectData((prev) => ({
                                                ...prev,
                                                checkpoints: updatedCheckpoints,
                                              }));
                                            }}
                                            className="border-gray-200 focus:border-blue-500"
                                          />
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="text"
                                            value={
                                              typeof rubric._inputPoints ===
                                              "string"
                                                ? rubric._inputPoints
                                                : Math.floor(
                                                    rubric.points ??
                                                      rubric.total_points ??
                                                      0,
                                                  ).toString()
                                            }
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              // Allow empty string or digits only
                                              if (
                                                val === "" ||
                                                /^\d+$/.test(val)
                                              ) {
                                                const updatedCheckpoints = [
                                                  ...projectData.checkpoints,
                                                ];
                                                if (
                                                  !updatedCheckpoints[
                                                    checkpointIndex
                                                  ]?.rubrics?.[rubricIndex]
                                                )
                                                  return;
                                                updatedCheckpoints[
                                                  checkpointIndex
                                                ].rubrics[
                                                  rubricIndex
                                                ]._inputPoints = val;
                                                setProjectData((prev) => ({
                                                  ...prev,
                                                  checkpoints:
                                                    updatedCheckpoints,
                                                }));
                                              }
                                            }}
                                            onBlur={(e) => {
                                              const val = e.target.value;
                                              const newPoints =
                                                val === ""
                                                  ? 0
                                                  : Math.floor(
                                                      parseInt(val, 10),
                                                    );
                                              const updatedCheckpoints = [
                                                ...projectData.checkpoints,
                                              ];
                                              if (
                                                !updatedCheckpoints[
                                                  checkpointIndex
                                                ]?.rubrics?.[rubricIndex]
                                              )
                                                return;
                                              const rubric =
                                                updatedCheckpoints[
                                                  checkpointIndex
                                                ].rubrics[rubricIndex];
                                              rubric.points = newPoints;
                                              rubric.total_points = newPoints;
                                              delete rubric._inputPoints;
                                              // If rubric has criteria array, update the first criterion or create one
                                              if (rubric.criteria) {
                                                if (
                                                  rubric.criteria.length === 0
                                                ) {
                                                  rubric.criteria = [
                                                    {
                                                      name:
                                                        rubric.name ||
                                                        "Criterion",
                                                      description:
                                                        rubric.description ||
                                                        "",
                                                      points: newPoints,
                                                    },
                                                  ];
                                                } else if (rubric.criteria[0]) {
                                                  rubric.criteria[0].points =
                                                    newPoints;
                                                }
                                              }
                                              setProjectData((prev) => ({
                                                ...prev,
                                                checkpoints: updatedCheckpoints,
                                              }));
                                            }}
                                            className="w-20 text-center border border-gray-300 rounded px-2 py-1"
                                          />

                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => {
                                              const updatedCheckpoints = [
                                                ...projectData.checkpoints,
                                              ];
                                              updatedCheckpoints[
                                                checkpointIndex
                                              ].rubrics = (
                                                updatedCheckpoints[
                                                  checkpointIndex
                                                ].rubrics || []
                                              ).filter(
                                                (_, i) => i !== rubricIndex,
                                              );
                                              setProjectData((prev) => ({
                                                ...prev,
                                                checkpoints: updatedCheckpoints,
                                              }));
                                            }}
                                            className="h-9 w-9 p-0 text-red-500 hover:bg-red-50"
                                          >
                                            <Trash2 className="h-4 w-4" />
                                          </Button>
                                        </div>
                                      </div>
                                    ),
                                  )
                                : []}

                              {(!Array.isArray(checkpoint.rubrics) ||
                                checkpoint.rubrics.length === 0) && (
                                <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
                                  <FileText className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                                  <p className="text-gray-600 mb-3">
                                    No rubrics defined for this checkpoint
                                  </p>
                                  <Button
                                    variant="outline"
                                    onClick={() => {
                                      const newRubric = {
                                        name: `Rubric ${(checkpoint.rubrics?.length || 0) + 1}`,
                                        description: "",
                                        points: 0,
                                      };
                                      const updatedCheckpoints = [
                                        ...projectData.checkpoints,
                                      ];
                                      if (
                                        !updatedCheckpoints[checkpointIndex]
                                          .rubrics
                                      ) {
                                        updatedCheckpoints[
                                          checkpointIndex
                                        ].rubrics = [];
                                      }
                                      updatedCheckpoints[
                                        checkpointIndex
                                      ].rubrics.push(newRubric);
                                      setProjectData((prev) => ({
                                        ...prev,
                                        checkpoints: updatedCheckpoints,
                                      }));
                                    }}
                                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add First Rubric
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                )}

                {rubricVariation === "detailed" && (
                  <div className="space-y-0">
                    {projectData?.checkpoints?.map(
                      (checkpoint, checkpointIndex) => (
                        <div
                          key={`rubric-checkpoint-${checkpointIndex}`}
                          className="border-b border-gray-100 last:border-b-0"
                        >
                          {/* Checkpoint Header */}
                          <div className="bg-gradient-to-r from-blue-50 to-gray-50 p-6 border-l-4 border-blue-500">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-3">
                                  <GripVertical className="h-5 w-5 text-gray-400" />
                                  <div>
                                    <h3 className="text-lg font-semibold text-gray-900">
                                      {checkpoint.title}
                                    </h3>
                                    <div className="flex items-center space-x-4 mt-1">
                                      <span className="text-sm text-gray-500">
                                        {Array.isArray(checkpoint.rubrics)
                                          ? checkpoint.rubrics.length
                                          : 0}{" "}
                                        rubrics •{" "}
                                        {Array.isArray(checkpoint.rubrics)
                                          ? checkpoint.rubrics.reduce(
                                              (sum, rubric) =>
                                                sum +
                                                Number(
                                                  rubric.total_points || 0,
                                                ),
                                              0,
                                            )
                                          : 0}{" "}
                                        points
                                      </span>
                                      <div className="flex items-center space-x-1">
                                        {[
                                          ...Array(
                                            Math.ceil(
                                              (Array.isArray(checkpoint.rubrics)
                                                ? checkpoint.rubrics.reduce(
                                                    (sum, rubric) =>
                                                      sum +
                                                      (rubric.points || 0),
                                                    0,
                                                  )
                                                : 0) / 10,
                                            ),
                                          ),
                                        ].map((_, i) => (
                                          <div
                                            key={i}
                                            className="w-2 h-2 bg-blue-300 rounded-full"
                                          ></div>
                                        ))}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              <Button
                                variant="outline"
                                onClick={() => {
                                  const newRubric = {
                                    name: `Rubric ${(checkpoint.rubrics?.length || 0) + 1}`,
                                    description: "",
                                    points: 0,
                                  };
                                  const updatedCheckpoints = [
                                    ...projectData.checkpoints,
                                  ];
                                  if (
                                    !updatedCheckpoints[checkpointIndex].rubrics
                                  ) {
                                    updatedCheckpoints[
                                      checkpointIndex
                                    ].rubrics = [];
                                  }
                                  updatedCheckpoints[
                                    checkpointIndex
                                  ].rubrics.push(newRubric);
                                  setProjectData((prev) => ({
                                    ...prev,
                                    checkpoints: updatedCheckpoints,
                                  }));
                                }}
                                className="bg-white border-blue-200 text-blue-600 hover:bg-blue-50 shadow-sm"
                              >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Rubric
                              </Button>
                            </div>
                          </div>

                          {/* Rubrics List */}
                          <div className="p-6 bg-white">
                            <div className="space-y-4">
                              {Array.isArray(checkpoint.rubrics)
                                ? checkpoint.rubrics.map(
                                    (rubric, rubricIndex) => (
                                      <div
                                        key={`rubric-${rubric.name || rubricIndex}`}
                                        className="group"
                                      >
                                        <div className="flex items-start gap-6 p-5 rounded-xl border-2 border-gray-100 hover:border-blue-200 hover:shadow-md transition-all duration-300 bg-gradient-to-r from-white to-gray-50">
                                          {/* Rubric Number & Drag Handle */}
                                          <div className="flex flex-col items-center space-y-2">
                                            <div className="w-10 h-10 flex items-center justify-center bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl shadow-sm font-semibold">
                                              {rubricIndex + 1}
                                            </div>
                                            <GripVertical className="h-4 w-4 text-gray-300 opacity-0 group-hover:opacity-100 transition-opacity" />
                                          </div>

                                          {/* Rubric Content */}
                                          <div className="flex-1 space-y-3">
                                            <div className="flex items-center space-x-2">
                                              <label className="text-sm font-medium text-gray-700">
                                                Description
                                              </label>
                                              <div className="flex-1 h-px bg-gray-200"></div>
                                            </div>
                                            <Input
                                              placeholder="Describe the grading rubric in detail (e.g., Data preprocessing, cleaning, and validation techniques)"
                                              value={rubric.description || ""}
                                              onChange={(e) => {
                                                const updatedCheckpoints = [
                                                  ...projectData.checkpoints,
                                                ];
                                                if (
                                                  !updatedCheckpoints[
                                                    checkpointIndex
                                                  ]?.rubrics?.[rubricIndex]
                                                )
                                                  return;
                                                updatedCheckpoints[
                                                  checkpointIndex
                                                ].rubrics[
                                                  rubricIndex
                                                ].description = e.target.value;
                                                setProjectData((prev) => ({
                                                  ...prev,
                                                  checkpoints:
                                                    updatedCheckpoints,
                                                }));
                                              }}
                                              className="border-gray-200 focus:border-blue-500 focus:ring-blue-500 bg-white shadow-sm"
                                            />
                                          </div>

                                          {/* Points & Actions */}
                                          <div className="flex flex-col items-center space-y-3">
                                            <div className="text-center">
                                              <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                Points
                                              </label>
                                              <div className="mt-1">
                                                <input
                                                  type="text"
                                                  value={
                                                    typeof rubric._inputPoints ===
                                                    "string"
                                                      ? rubric._inputPoints
                                                      : Math.floor(
                                                          rubric.points ??
                                                            rubric.total_points ??
                                                            0,
                                                        ).toString()
                                                  }
                                                  onChange={(e) => {
                                                    const val = e.target.value;
                                                    // Allow empty string or digits only
                                                    if (
                                                      val === "" ||
                                                      /^\d+$/.test(val)
                                                    ) {
                                                      const updatedCheckpoints =
                                                        [
                                                          ...projectData.checkpoints,
                                                        ];
                                                      if (
                                                        !updatedCheckpoints[
                                                          checkpointIndex
                                                        ]?.rubrics?.[
                                                          rubricIndex
                                                        ]
                                                      )
                                                        return;
                                                      updatedCheckpoints[
                                                        checkpointIndex
                                                      ].rubrics[
                                                        rubricIndex
                                                      ]._inputPoints = val;
                                                      setProjectData(
                                                        (prev) => ({
                                                          ...prev,
                                                          checkpoints:
                                                            updatedCheckpoints,
                                                        }),
                                                      );
                                                    }
                                                  }}
                                                  onBlur={(e) => {
                                                    const val = e.target.value;
                                                    const newPoints =
                                                      val === ""
                                                        ? 0
                                                        : Math.floor(
                                                            parseInt(val, 10),
                                                          );

                                                    const updatedCheckpoints = [
                                                      ...projectData.checkpoints,
                                                    ];

                                                    const checkpoint =
                                                      updatedCheckpoints[
                                                        checkpointIndex
                                                      ];
                                                    if (
                                                      !checkpoint ||
                                                      !checkpoint.rubrics
                                                    )
                                                      return;

                                                    const rubric =
                                                      checkpoint.rubrics[
                                                        rubricIndex
                                                      ];
                                                    if (!rubric) return;

                                                    rubric.points = newPoints;
                                                    rubric.total_points =
                                                      newPoints;

                                                    delete rubric._inputPoints;
                                                    if (rubric.criteria) {
                                                      if (
                                                        rubric.criteria
                                                          .length === 0
                                                      ) {
                                                        rubric.criteria = [
                                                          {
                                                            name:
                                                              rubric.name ||
                                                              "Criterion",
                                                            description:
                                                              rubric.description ||
                                                              "",
                                                            points: newPoints,
                                                          },
                                                        ];
                                                      } else {
                                                        rubric.criteria[0].points =
                                                          newPoints;
                                                      }
                                                    }
                                                    setProjectData((prev) => ({
                                                      ...prev,
                                                      checkpoints:
                                                        updatedCheckpoints,
                                                    }));
                                                  }}
                                                  className="w-20 text-center border border-gray-300 rounded px-2 py-1"
                                                />
                                              </div>
                                            </div>

                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() => {
                                                const updatedCheckpoints = [
                                                  ...projectData.checkpoints,
                                                ];
                                                if (
                                                  !updatedCheckpoints[
                                                    checkpointIndex
                                                  ]?.rubrics
                                                )
                                                  return;
                                                updatedCheckpoints[
                                                  checkpointIndex
                                                ].rubrics = (
                                                  updatedCheckpoints[
                                                    checkpointIndex
                                                  ].rubrics || []
                                                ).filter(
                                                  (_, i) => i !== rubricIndex,
                                                );
                                                setProjectData((prev) => ({
                                                  ...prev,
                                                  checkpoints:
                                                    updatedCheckpoints,
                                                }));
                                              }}
                                              className="opacity-0 group-hover:opacity-100 h-9 w-9 p-0 text-gray-400 hover:text-red-500 hover:bg-red-50 transition-all"
                                            >
                                              <Trash2 className="h-4 w-4" />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    ),
                                  )
                                : []}

                              {/* Enhanced Empty State */}
                              {(!Array.isArray(checkpoint.rubrics) ||
                                checkpoint.rubrics.length === 0) && (
                                <div className="text-center py-12 bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl border-2 border-dashed border-gray-200">
                                  <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
                                    <FileText className="h-8 w-8 text-blue-500" />
                                  </div>
                                  <h4 className="font-semibold text-gray-900 mb-2">
                                    No rubrics defined yet
                                  </h4>
                                  <p className="text-gray-600 mb-4 max-w-md mx-auto">
                                    Add grading rubrics to define how students
                                    will be evaluated for this checkpoint
                                  </p>
                                  <Button
                                    variant="outline"
                                    onClick={() => {
                                      const newRubric = {
                                        name: `Rubric ${(checkpoint.rubrics?.length || 0) + 1}`,
                                        description: "",
                                        points: 0,
                                      };
                                      const updatedCheckpoints = [
                                        ...projectData.checkpoints,
                                      ];
                                      if (
                                        !updatedCheckpoints[checkpointIndex]
                                          .rubrics
                                      ) {
                                        updatedCheckpoints[
                                          checkpointIndex
                                        ].rubrics = [];
                                      }
                                      updatedCheckpoints[
                                        checkpointIndex
                                      ].rubrics.push(newRubric);
                                      setProjectData((prev) => ({
                                        ...prev,
                                        checkpoints: updatedCheckpoints,
                                      }));
                                    }}
                                    className="border-blue-200 text-blue-600 hover:bg-blue-50"
                                  >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Add First Rubric
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                )}
                {/* Enhanced Status & Validation */}
                <div className="p-6 bg-gradient-to-r from-gray-50 to-blue-50 border-t border-gray-100">
                  {!isValidMarks || totalMarks !== targetTotal ? (
                    <div
                      className={`p-5 rounded-xl border-2 mb-4 ${
                        totalMarks > targetTotal
                          ? "bg-gradient-to-r from-red-50 to-red-100 border-red-200"
                          : totalMarks === targetTotal
                            ? "bg-gradient-to-r from-green-50 to-emerald-100 border-green-200"
                            : "bg-gradient-to-r from-yellow-50 to-yellow-100 border-yellow-200"
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div
                          className={`p-2 rounded-lg ${
                            totalMarks > targetTotal
                              ? "bg-red-200"
                              : totalMarks === targetTotal
                                ? "bg-green-200"
                                : "bg-yellow-200"
                          }`}
                        >
                          {totalMarks === targetTotal ? (
                            <CheckCircle className="h-6 w-6 text-green-600" />
                          ) : (
                            <AlertTriangle
                              className={`h-6 w-6 ${
                                totalMarks > targetTotal
                                  ? "text-red-600"
                                  : "text-yellow-600"
                              }`}
                            />
                          )}
                        </div>
                        <div className="flex-1">
                          <h4
                            className={`font-semibold mb-1 ${
                              totalMarks > targetTotal
                                ? "text-red-800"
                                : totalMarks === targetTotal
                                  ? "text-green-800"
                                  : "text-yellow-800"
                            }`}
                          >
                            {totalMarks > targetTotal
                              ? "Points Exceed Maximum"
                              : totalMarks === targetTotal
                                ? "Rubric Complete"
                                : "Rubric Incomplete"}
                          </h4>
                          <p
                            className={`text-sm ${
                              totalMarks > targetTotal
                                ? "text-red-700"
                                : totalMarks === targetTotal
                                  ? "text-green-700"
                                  : "text-yellow-700"
                            }`}
                          >
                            {totalMarks > targetTotal
                              ? `Total marks exceed ${targetTotal} by ${totalMarks - targetTotal} marks`
                              : totalMarks < targetTotal
                                ? `Total marks are ${targetTotal - totalMarks} marks short of ${targetTotal}`
                                : ""}
                          </p>
                          <div className="mt-3 flex items-center space-x-4">
                            <div className="text-sm">
                              <span className="font-medium">
                                Current total:
                              </span>{" "}
                              {totalMarks} points
                            </div>
                            <div className="text-sm">
                              <span className="font-medium">Target:</span>{" "}
                              {targetTotal} points
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-5 rounded-xl bg-gradient-to-r from-green-50 to-emerald-100 border-2 border-green-200 mb-4">
                      <div className="flex items-start gap-4">
                        <div className="p-2 bg-green-200 rounded-lg">
                          <CheckCircle className="h-6 w-6 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-semibold text-green-800 mb-1">
                            Perfect Rubric!
                          </h4>
                          <p className="text-sm text-green-700">
                            Your grading rubric is complete and correctly totals{" "}
                            {targetTotal} points across all checkpoints.
                          </p>
                          {/* <div className="mt-3 flex items-center space-x-4">
                            <div className="text-sm text-green-600">
                              <span className="font-medium">Total criteria:</span>{" "}
                              {projectData?.gradingRubric?.reduce(
                                (total, item) =>
                                  total + (Array.isArray(item.criteria) ? item.criteria.length : 0),
                                0
                              )}
                            </div>
                            <div className="text-sm text-green-600">
                              <span className="font-medium">Checkpoints:</span>{" "}
                              {projectData?.gradingRubric?.length}
                            </div>
                          </div> */}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Rubric Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    {projectData?.checkpoints?.map((checkpoint, index) => {
                      // Calculate total criteria count and points for this checkpoint
                      const totalCriteria = Array.isArray(checkpoint.rubrics)
                        ? checkpoint.rubrics.reduce((sum, rubric) => {
                            if (
                              rubric.criteria &&
                              Array.isArray(rubric.criteria)
                            ) {
                              return sum + rubric.criteria.length;
                            }
                            return sum + 1; // Count as 1 criterion if no criteria array
                          }, 0)
                        : 0;

                      const total_points = Array.isArray(checkpoint.rubrics)
                        ? checkpoint.rubrics.reduce((sum, rubric) => {
                            // If rubric has criteria array, sum from criteria points
                            if (
                              rubric.criteria &&
                              Array.isArray(rubric.criteria) &&
                              rubric.criteria.length > 0
                            ) {
                              return (
                                sum +
                                rubric.criteria.reduce(
                                  (criteriaSum, criterion) => {
                                    return (
                                      criteriaSum + (criterion.points ?? 0)
                                    );
                                  },
                                  0,
                                )
                              );
                            }
                            // Fallback to rubric total_points or points if no criteria
                            return (
                              sum + (rubric.total_points ?? rubric.points ?? 0)
                            );
                          }, 0)
                        : 0;

                      return (
                        <div
                          key={index}
                          className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm"
                        >
                          <h5
                            className="font-medium text-gray-900 text-sm mb-2 truncate"
                            title={checkpoint?.title}
                          >
                            {checkpoint?.title}
                          </h5>
                          <div className="flex items-center justify-between">
                            <span className="text-xs text-gray-500">
                              {totalCriteria} criteria
                            </span>
                            <span className="font-semibold text-blue-600">
                              {total_points} pts
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    );
  },
);

ProjectSettingsStep.displayName = "ProjectSettingsStep";
